import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

export const getSuppliersList = createAsyncThunk(
    'suppliers/getSuppliersList',
    async ({ accountId, token, search, start, length, hasBrands }, { rejectWithValue }) => {
    try {
        let url = `${import.meta.env.VITE_API_BASE_URL}/suppliers/list?accountId=${accountId}&start=${start}&length=${length}&draw=1`;
        
        // Aggiungo i parametri di ricerca se presenti
        if (search && search.length > 0) {
            url += `&search[value]=${encodeURIComponent(search)}`;
        } else {
            url += `&search[value]=`;
        }
        
        // Aggiungo filtri opzionali
        if (hasBrands !== undefined) {
            url += `&hasBrands=${hasBrands}`;
        }
        
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'auth-token': token
            }
        });
        
        if (!response.ok) {
            const errorData = await response.json();
            return rejectWithValue(errorData.message || 'Errore durante il recupero dei fornitori');
        }
        
        const data = await response.json();
        return data;
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

export const createSupplier = createAsyncThunk(
    'suppliers/createSupplier',
    async ({ supplier, token }, { rejectWithValue }) => {
    try {
        const url = `${import.meta.env.VITE_API_BASE_URL}/suppliers`;
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'auth-token': token
            },
            body: JSON.stringify({ supplier })
        });
        if (!response.ok) {
            const errorData = await response.json();
            return rejectWithValue(errorData.message || 'Errore durante la creazione del fornitore');
        }
        const data = await response.json();
        return data.data;
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

export const updateSupplier = createAsyncThunk(
    'suppliers/updateSupplier',
    async ({ supplier, token }, { rejectWithValue }) => {
    try {
        const url = `${import.meta.env.VITE_API_BASE_URL}/suppliers`;
        const response = await fetch(url, {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
                'auth-token': token
            },
            body: JSON.stringify({ supplier })
        });
        if (!response.ok) {
            const errorData = await response.json();
            return rejectWithValue(errorData.message || 'Errore durante l\'aggiornamento del fornitore');
        }
        const data = await response.json();
        return data.data;
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

export const deleteSupplier = createAsyncThunk(
    'suppliers/deleteSupplier',
    async ({ supplierId, token }, { rejectWithValue }) => {
    try {
        const url = `${import.meta.env.VITE_API_BASE_URL}/suppliers?supplierId=${supplierId}`;
        const response = await fetch(url, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                'auth-token': token
            }
        });
        if (!response.ok) {
            const errorData = await response.json();
            return rejectWithValue(errorData.message || 'Errore durante l\'eliminazione del fornitore');
        }
        return supplierId;
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

export const getSuppliersAll = createAsyncThunk(
    'suppliers/getSuppliersAll',
    async ({ accountId, token }, { rejectWithValue }) => {
    try {
        const url = `${import.meta.env.VITE_API_BASE_URL}/suppliers/suppliersList?accountId=${accountId}`;
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'auth-token': token
            }
        });
        if (!response.ok) {
            const errorData = await response.json();
            return rejectWithValue(errorData.message || 'Errore durante il recupero dei fornitori');
        }
        const data = await response.json();
        return Array.isArray(data) ? data : (data.data || []);
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

export const getSupplierDetails = createAsyncThunk(
    'suppliers/getSupplierDetails',
    async ({ supplierId, token }, { rejectWithValue }) => {
    try {
        const url = `${import.meta.env.VITE_API_BASE_URL}/suppliers?supplierId=${supplierId}`;

        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'auth-token': token
            }
        });

        if (!response.ok) {
            const errorData = await response.json();
            return rejectWithValue(errorData.message || 'Errore durante il recupero dei dettagli del fornitore');
        }

        const data = await response.json();
        return data.data || data;
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

const suppliersSlice = createSlice({
    name: 'suppliers',
    initialState: {
        data: null,
        loading: false,
        error: null,
        supplierDetails: null,
        loadingDetails: false,
        errorDetails: null,
        createLoading: false,
        createError: null,
        updateLoading: false,
        updateError: null,
        deleteLoading: false,
        deleteError: null,
        suppliersAll: [],
        suppliersAllLoading: false,
        suppliersAllError: null
    },
    reducers: {
        resetSupplierDetails: (state) => {
            state.supplierDetails = null;
            state.loadingDetails = false;
            state.errorDetails = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // getSuppliersList
            .addCase(getSuppliersList.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getSuppliersList.fulfilled, (state, action) => {
                state.loading = false;
                state.data = action.payload;
            })
            .addCase(getSuppliersList.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Errore durante il recupero dei fornitori';
            })
            // createSupplier
            .addCase(createSupplier.pending, (state) => {
                state.createLoading = true;
                state.createError = null;
            })
            .addCase(createSupplier.fulfilled, (state) => {
                state.createLoading = false;
            })
            .addCase(createSupplier.rejected, (state, action) => {
                state.createLoading = false;
                state.createError = action.payload;
            })
            // updateSupplier
            .addCase(updateSupplier.pending, (state) => {
                state.updateLoading = true;
                state.updateError = null;
            })
            .addCase(updateSupplier.fulfilled, (state) => {
                state.updateLoading = false;
            })
            .addCase(updateSupplier.rejected, (state, action) => {
                state.updateLoading = false;
                state.updateError = action.payload;
            })
            // deleteSupplier
            .addCase(deleteSupplier.pending, (state) => {
                state.deleteLoading = true;
                state.deleteError = null;
            })
            .addCase(deleteSupplier.fulfilled, (state) => {
                state.deleteLoading = false;
            })
            .addCase(deleteSupplier.rejected, (state, action) => {
                state.deleteLoading = false;
                state.deleteError = action.payload;
            })
            // getSuppliersAll
            .addCase(getSuppliersAll.pending, (state) => {
                state.suppliersAllLoading = true;
                state.suppliersAllError = null;
            })
            .addCase(getSuppliersAll.fulfilled, (state, action) => {
                state.suppliersAllLoading = false;
                state.suppliersAll = action.payload;
            })
            .addCase(getSuppliersAll.rejected, (state, action) => {
                state.suppliersAllLoading = false;
                state.suppliersAllError = action.payload;
            })
            // getSupplierDetails
            .addCase(getSupplierDetails.pending, (state) => {
                state.loadingDetails = true;
                state.errorDetails = null;
            })
            .addCase(getSupplierDetails.fulfilled, (state, action) => {
                state.loadingDetails = false;
                state.supplierDetails = action.payload;
            })
            .addCase(getSupplierDetails.rejected, (state, action) => {
                state.loadingDetails = false;
                state.errorDetails = action.payload || 'Errore durante il recupero dei dettagli del fornitore';
            });
    }
});

export const { resetSupplierDetails } = suppliersSlice.actions;

export const selectSuppliersData = (state) => state.suppliers.data;
export const selectSuppliersLoading = (state) => state.suppliers.loading;
export const selectSuppliersError = (state) => state.suppliers.error;
export const selectSupplierDetails = (state) => state.suppliers.supplierDetails;
export const selectSupplierDetailsLoading = (state) => state.suppliers.loadingDetails;
export const selectSupplierDetailsError = (state) => state.suppliers.errorDetails;

export const selectSupplierCreateLoading = (state) => state.suppliers.createLoading;
export const selectSupplierCreateError = (state) => state.suppliers.createError;
export const selectSupplierUpdateLoading = (state) => state.suppliers.updateLoading;
export const selectSupplierUpdateError = (state) => state.suppliers.updateError;
export const selectSupplierDeleteLoading = (state) => state.suppliers.deleteLoading;
export const selectSupplierDeleteError = (state) => state.suppliers.deleteError;
export const selectSuppliersAll = (state) => state.suppliers.suppliersAll;
export const selectSuppliersAllLoading = (state) => state.suppliers.suppliersAllLoading;

export default suppliersSlice.reducer;
